import React, { FC } from 'react';

import { Alert, Button, Label, Link, TextInput, withToaster } from '@gravity-ui/uikit';
import { validateASCII, validateCompound, validateLength, validateURL } from '@paskills/validation';
import i18n from '@yandex-int/i18n';
import { ApiError, createOrUpdateOAuthApp } from 'client/api';
import { IconMedium } from 'client/blocks/AppIcons/AppIcons';
import { Field } from 'client/blocks/form/Field/Field';
import formContainer, { FormContainerProps } from 'client/hocs/form-container';
import { InjectedFormProps } from 'client/hocs/types';
import { UpdateOAuthAppSettings } from 'client/model/oauth';
import { Skill } from 'client/model/skill';
import labels from 'client/utils/settings/oauth-labels';
import { addToast } from 'client/utils/settings/toaster';
import * as keyset from 'i18n/AccountLinking';
import * as keysetCommon from 'i18n/Common';
import isEmpty from 'lodash/isEmpty';

import styles from './AliceAppAccountLinkingForm.module.scss';

interface OuterProps {
    settings: UpdateOAuthAppSettings;
    skill: Skill;
    onUpdate: (_id: string) => void;
    name: string;
    disabled: boolean;
}

type Props = InjectedFormProps<UpdateOAuthAppSettings, OuterProps, { rateLimit: string }>;

const EMPTY_FIELD = '*****************';
const DOCS = 'https://tools.ietf.org/html/rfc6749#section-';

const t = i18n(keyset);
const tCommon = i18n(keysetCommon);

function validate(params: UpdateOAuthAppSettings) {
    const hasRequiredFields = [
        params.clientId,
        params.authorizationUrl,
        !params.id ? params.clientSecret : true,
        params.tokenUrl,
    ].every(Boolean);

    return hasRequiredFields;
}

const AliceAppAccountLinkingForm: FC<Props> = (props) => {
    const { skill, values, isSubmitting, errors, onChange, isNonValidationError, touched, disabled } = props;

    const isValid = props.isValid && validate(values);

    const submitMessage = isNonValidationError
        ? t('error-occured')
        : props.isValid
          ? ''
          : errors.rateLimit || t('error-form');
    const submitSeverity = !props.isValid || isNonValidationError ? 'error' : 'info';
    return (
        <>
            <header className={styles.aliceAppAccountLinkingForm__header}>
                <div className={styles.aliceAppAccountLinkingForm__header__titleBlock}>
                    <IconMedium imageUrl={skill.draft?.logo2?.url || skill.logo2?.url} />
                    <h1 className={styles.aliceAppAccountLinkingForm__title}>{skill.draft?.name}</h1>
                </div>
                <div className={styles.aliceAppAccountLinkingForm__buttons}>
                    <Button
                        className={styles.aliceAppAccountLinkingForm__saveButton}
                        type="submit"
                        view="action"
                        loading={isSubmitting}
                        disabled={disabled || !isValid}
                    >
                        {tCommon('save')}
                    </Button>
                </div>
            </header>
            <section className={styles.aliceAppAccountLinkingForm__content}>
                <h2 className={styles.aliceAppAccountLinkingForm__pageTitle}>{t('account-linking')}</h2>
                {!isEmpty(touched) && values.id && (
                    <Alert
                        className={styles.aliceAppAccountLinkingForm__alert}
                        theme="warning"
                        title={
                            <span className={styles.aliceAppAccountLinkingForm__alertTitle}>
                                {t('warning-settings')}
                            </span>
                        }
                    />
                )}
                <div className={styles.aliceAppAccountLinkingForm__warningsBlock}>
                    {!!submitMessage && (
                        <Label theme={submitSeverity === 'error' ? 'danger' : 'info'}>{submitMessage}</Label>
                    )}
                </div>
                <Field
                    required
                    fieldTitle={t('label-client_id')}
                    error={errors.clientId}
                    fieldDescription={
                        <Link href={`${DOCS}2.2`} target="_blank">
                            Client Identifier
                        </Link>
                    }
                >
                    <TextInput
                        className={styles.aliceAppAccountLinkingForm__formElement}
                        value={values.clientId}
                        onUpdate={onChange('clientId')}
                        size="l"
                        disabled={disabled}
                    />
                </Field>

                <Field
                    required={!values.id}
                    fieldTitle={t('label-client_secret')}
                    error={errors.clientSecret}
                    fieldDescription={
                        <Link href={`${DOCS}2.3.1`} target="_blank">
                            Client Password
                        </Link>
                    }
                >
                    <TextInput
                        className={styles.aliceAppAccountLinkingForm__formElement}
                        value={values.clientSecret}
                        onUpdate={onChange('clientSecret')}
                        placeholder={values.id && isEmpty(values.clientSecret) ? EMPTY_FIELD : undefined}
                        size="l"
                        disabled={disabled}
                    />
                </Field>

                <Field
                    required
                    fieldTitle={t('label-auth_url')}
                    error={errors.authorizationUrl}
                    fieldDescription={
                        <Link href={`${DOCS}3.1`} target="_blank">
                            API authorization endpoint
                        </Link>
                    }
                >
                    <TextInput
                        className={styles.aliceAppAccountLinkingForm__formElement}
                        value={values.authorizationUrl}
                        onUpdate={onChange('authorizationUrl')}
                        type="url"
                        size="l"
                        disabled={disabled}
                    />
                </Field>

                <Field
                    required
                    fieldTitle={t('label-token_url')}
                    error={errors.tokenUrl}
                    fieldDescription={
                        <Link href={`${DOCS}3.2`} target="_blank">
                            Token Endpoint
                        </Link>
                    }
                >
                    <TextInput
                        className={styles.aliceAppAccountLinkingForm__formElement}
                        value={values.tokenUrl}
                        onUpdate={onChange('tokenUrl')}
                        type="url"
                        size="l"
                        disabled={disabled}
                    />
                </Field>

                <Field
                    fieldTitle={t('label-refresh_token_url')}
                    error={errors.refreshTokenUrl}
                    fieldDescription={
                        <Link href={`${DOCS}6`} target="_blank">
                            Refreshing an Access Token
                        </Link>
                    }
                >
                    <TextInput
                        className={styles.aliceAppAccountLinkingForm__formElement}
                        value={values.refreshTokenUrl}
                        onUpdate={onChange('refreshTokenUrl')}
                        type="url"
                        size="l"
                        disabled={disabled}
                    />
                </Field>

                <Field
                    fieldTitle={t('label-scope')}
                    error={errors.scope}
                    fieldDescription={
                        <Link href={`${DOCS}3.3`} target="_blank">
                            Access Token Scope
                        </Link>
                    }
                >
                    <TextInput
                        className={styles.aliceAppAccountLinkingForm__formElement}
                        value={values.scope}
                        onUpdate={onChange('scope')}
                        size="l"
                        disabled={disabled}
                    />
                </Field>
            </section>
        </>
    );
};

const AliceAppAccountLinking = formContainer<OuterProps, UpdateOAuthAppSettings>({
    mapPropsToValues: (props) => ({ ...props.settings }),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const { id } = await createOrUpdateOAuthApp({ ...values, name: props.name });
            props.onUpdate(id);
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.fields) {
                    // @ts-expect-error legacy
                    await new Promise((resolve) => setErrors(err.fields, resolve));
                }
            } else {
                console.error(err);
            }
            addToast(props, { title: t('error-occured'), theme: 'danger' });

            throw err;
        }
    },
    setupValidationSchema: () => {
        return {
            clientId: (value: string) => ({
                clientId: validateCompound(
                    validateASCII(value, labels.clientId) && t('error-ascii-app_id'),
                    validateLength(value, 100, labels.clientId) && t('error-length-app_id'),
                ),
            }),
            clientSecret: (value: string) => ({
                clientSecret: validateCompound(
                    validateASCII(value, labels.clientSecret) && t('error-ascii-app_secret'),
                    validateLength(value, 100, labels.clientSecret) && t('error-length-app_secret'),
                ),
            }),
            authorizationUrl: (value: string) => ({
                authorizationUrl: validateCompound(
                    validateURL(value, true) && t('error-invalid_URL'),
                    validateLength(value, 200, labels.authorizationUrl) && t('error-length-authorisation'),
                ),
            }),
            tokenUrl: (value: string) => ({
                tokenUrl: validateCompound(
                    validateURL(value, true) && t('error-invalid_URL'),
                    validateLength(value, 200, labels.tokenUrl) && t('error-length-token'),
                ),
            }),
            refreshTokenUrl: (value?: string) => ({
                refreshTokenUrl: value
                    ? validateCompound(
                          validateURL(value, true) && t('error-invalid_URL'),
                          validateLength(value, 200, labels.refreshTokenUrl) && t('error-length-refresh-token'),
                      )
                    : '',
            }),
            scope: (value?: string) => ({
                scope: value
                    ? validateCompound(
                          validateASCII(value, labels.scope) && t('error-ascii-activity'),
                          validateLength(value, 500, labels.scope) && t('error-length-activity'),
                      )
                    : '',
            }),
            yandexClientId: (value?: string) => ({
                yandexClientId: value
                    ? validateCompound(
                          validateASCII(value, labels.yandexClientId) && t('error-ascii-yandex_id'),
                          validateLength(value, 100, labels.yandexClientId) && t('error-length-yandex_id'),
                      )
                    : '',
            }),
        };
    },
    showPrompt: false,
})(AliceAppAccountLinkingForm);

const AliceAppAccountLinkingWithToaster = withToaster<OuterProps & FormContainerProps>()(AliceAppAccountLinking);

export default AliceAppAccountLinkingWithToaster;
